export const STATUS_COLORS = {
  New: 'primary',
  'In Progress': 'warning',
  Converted: 'success',
  Lost: 'error'
};

export const normalizePhone = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

export const normalizeEmail = (email) => {
  if (!email) return '';
  return String(email).trim().toLowerCase();
};

export const isValidPhone = (phone) => {
  if (!phone) return false;
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 10) return false;
  const last10 = digits.slice(-10);
  // Ignore dummy phone numbers like all same digits (0000000000, 9999999999) or sequential dummy numbers
  if (/^(\d)\1{9}$/.test(last10)) return false;
  if (last10 === '1234567890' || last10 === '0123456789') return false;
  return true;
};

export const isValidEmail = (email) => {
  if (!email) return false;
  const clean = String(email).trim().toLowerCase();
  if (['n/a', 'na', 'none', 'null', 'undefined', 'no', '-', 'nil', 'noemail', 'no@email.com'].includes(clean)) {
    return false;
  }
  // Must be a plausible email pattern
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean);
};

/**
 * Robust & safe customer grouping:
 * Groups by normalized 10-digit phone number if valid.
 * If phone is invalid/missing, groups by valid email address.
 * Never chains across unrelated leads using loose union find.
 */
export const groupLeadsByCustomer = (leadsList) => {
  if (!Array.isArray(leadsList) || leadsList.length === 0) return [];

  const groupsMap = new Map();

  for (const lead of leadsList) {
    let groupKey = null;

    if (isValidPhone(lead.guest_phone)) {
      const phoneDigits = normalizePhone(lead.guest_phone);
      groupKey = `phone:${phoneDigits}`;
    } else if (isValidEmail(lead.guest_email)) {
      const cleanEmail = normalizeEmail(lead.guest_email);
      groupKey = `email:${cleanEmail}`;
    } else {
      groupKey = `id:${lead._id}`;
    }

    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, []);
    }
    groupsMap.get(groupKey).push(lead);
  }

  // Convert to array and enrich group statistics
  const groups = Array.from(groupsMap.entries()).map(([groupKey, clusterLeads]) => {
    // Sort leads in this cluster by createdAt descending (newest first)
    clusterLeads.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    const latestLead = clusterLeads[0];
    const guest_name = clusterLeads.find(l => l.guest_name && l.guest_name.trim())?.guest_name || latestLead.guest_name || 'Unnamed Guest';
    const guest_phone = clusterLeads.find(l => l.guest_phone && l.guest_phone.trim())?.guest_phone || latestLead.guest_phone || '—';
    const guest_email = clusterLeads.find(l => l.guest_email && l.guest_email.trim())?.guest_email || latestLead.guest_email || '—';
    const guest_address = clusterLeads.find(l => l.guest_address && l.guest_address.trim())?.guest_address || latestLead.guest_address || '';

    // Status tally
    const statusCounts = {};
    for (const l of clusterLeads) {
      const s = l.status || 'New';
      statusCounts[s] = (statusCounts[s] || 0) + 1;
    }

    // Unique assigned users
    const assigneeMap = new Map();
    for (const l of clusterLeads) {
      if (Array.isArray(l.assigned_to)) {
        for (const u of l.assigned_to) {
          const uid = String(u?._id || u?.id || u);
          if (uid && !assigneeMap.has(uid)) {
            assigneeMap.set(uid, typeof u === 'object' && u.fullName ? u : { id: uid, _id: uid, fullName: u.fullName || 'User' });
          }
        }
      }
    }
    const assignedUsers = Array.from(assigneeMap.values());
    const hasMultipleAssignees = assignedUsers.length > 1;

    const totalQuotes = clusterLeads.reduce((sum, l) => sum + (l.quotationsCount || 0), 0);
    const totalCalls = clusterLeads.reduce((sum, l) => sum + (l.call_history?.length || 0), 0);

    return {
      customerKey: groupKey,
      guest_name,
      guest_phone,
      guest_email,
      guest_address,
      leads: clusterLeads,
      inquiriesCount: clusterLeads.length,
      isMultiLead: clusterLeads.length > 1,
      statusCounts,
      latestStatus: latestLead.status || 'New',
      assignedUsers,
      hasMultipleAssignees,
      totalQuotes,
      totalCalls,
      latestCreatedAt: latestLead.createdAt || null,
      primaryLeadId: latestLead._id,
    };
  });

  // Sort groups by latest activity (createdAt descending)
  groups.sort((a, b) => new Date(b.latestCreatedAt || 0) - new Date(a.latestCreatedAt || 0));

  return groups;
};
